import { useState, useCallback, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { validateImageFile, formatFileSize } from '@/utils/helpers'
import Button from '@/components/common/Button'

export interface UploadZoneProps {
  onFileSelect: (file: File) => void
  onFileRemove: () => void
  file: File | null
  preview: string | null
  disabled?: boolean
}

const UploadZone = ({
  onFileSelect,
  onFileRemove,
  file,
  preview,
  disabled = false,
}: UploadZoneProps) => {
  const [isDragging, setIsDragging] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!disabled) setIsDragging(true)
  }, [disabled])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)

      if (disabled) return

      const droppedFile = e.dataTransfer.files[0]
      if (droppedFile) {
        handleFile(droppedFile)
      }
    },
    [disabled]
  )

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0]
      if (selectedFile) {
        handleFile(selectedFile)
      }
    },
    []
  )

  const handleFile = async (selectedFile: File) => {
    setError(null)

    const validation = validateImageFile(selectedFile)
    if (!validation.valid) {
      setError(validation.error || 'Invalid file')
      return
    }

    onFileSelect(selectedFile)
  }

  const handleClick = () => {
    if (!disabled && !file) {
      fileInputRef.current?.click()
    }
  }

  const handleRemove = () => {
    setError(null)
    onFileRemove()
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <div className="w-full">
      <AnimatePresence mode="wait">
        {!file ? (
          <motion.div
            key="upload-zone"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            <div
              className={`
                relative border-2 border-dashed rounded-xl p-8
                transition-all duration-300 cursor-pointer
                ${
                  isDragging
                    ? 'border-primary-500 bg-primary-500/10 scale-105 shadow-glow-md'
                    : 'border-gray-300 dark:border-dark-700 bg-gray-100 dark:bg-dark-800/50 hover:border-primary-500/50 hover:bg-primary-500/5'
                }
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
              `}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={handleClick}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/jpg"
                onChange={handleFileInput}
                disabled={disabled}
                className="hidden"
                aria-label="Upload chess board image"
              />

              <div className="flex flex-col items-center gap-4 text-center">
                <motion.div
                  animate={isDragging ? { scale: 1.1, rotate: 5 } : { scale: 1, rotate: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <svg
                    className="w-16 h-16 text-primary-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                </motion.div>

                <div>
                  <p className="text-lg font-semibold text-gray-900 dark:text-dark-50 mb-1">
                    {isDragging ? 'Drop image here' : 'Drag & drop image'}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-dark-400">
                    or click to browse • JPEG, PNG • Max 10MB
                  </p>
                </div>
              </div>

              {isDragging && (
                <motion.div
                  className="absolute inset-0 border-2 border-primary-500 rounded-xl"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  style={{
                    background:
                      'radial-gradient(circle at center, rgba(59, 130, 246, 0.1) 0%, transparent 70%)',
                  }}
                />
              )}
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="preview"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="relative"
          >
            <div className="bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-xl p-4">
              <div className="flex items-start gap-4">
                {preview && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.1, duration: 0.3 }}
                    className="flex-shrink-0"
                  >
                    <img
                      src={preview}
                      alt="Preview"
                      className="w-24 h-24 object-cover rounded-lg border border-gray-200 dark:border-dark-700"
                    />
                  </motion.div>
                )}

                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-gray-900 dark:text-dark-50 truncate mb-1">
                    {file.name}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-dark-400">
                    {formatFileSize(file.size)}
                  </p>
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRemove}
                  aria-label="Remove file"
                  className="flex-shrink-0"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mt-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg"
          >
            <p className="text-sm text-red-400">{error}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default UploadZone
